import { useMemo, useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../lib/convexApi";

// Egress allow-list management. The F2 backend (convex/egress) owns the
// curated `defaults` host list plus org-custom entries scoped either
// org-wide ("*") or to a single repo (the owner/repo key). This admin tab mirrors the
// firewall-rules tab's chip-based density + design tokens — no parallel
// visual system (see the admin-UI quality bar).
//
// The Convex shapes are read loosely (api is the anyApi Proxy from
// lib/convexApi) so tsc doesn't have to follow the generated server types
// during the React build.

// SAME validation the backend mutation uses — bare hostname, an optional
// "*." wildcard prefix, or a dotted IPv4. We lowercase the host first so
// the case-insensitive flag isn't doing the work the backend's stored
// (lowercased) form expects.
const HOST_RE = /^(?:\*\.)?(?:[a-z0-9-]+\.)+[a-z0-9-]+$|^(?:\d{1,3}\.){3}\d{1,3}$/i;

const ORG_SCOPE = "*";

// A repo-scoped row is matched by EXACT equality against the session's
// repository key, which is the GitHub "owner/repo" full_name (e.g. "acme/api"
// — see sessionEngine + the webhook's payload.repository.full_name), NOT a full
// URL. So normalize a pasted URL / git-SSH / host-prefixed string down to
// owner/repo; otherwise an admin following a "paste the repo URL" instinct
// would store `https://github.com/acme/api`, which can never equal `acme/api`
// and the entry would silently never apply. (Codex P2.)
const REPO_SCOPE_RE = /^[^/\s]+\/[^/\s]+$/;

function normalizeRepoScope(input: string): string {
  let s = input.trim();
  if (!s) return "";
  s = s
    .replace(/^git@[^:]+:/i, "") // git@github.com:acme/api(.git) → acme/api(.git)
    .replace(/^[a-z][a-z0-9+.-]*:\/\//i, "") // strip scheme://
    .replace(/\.git$/i, "")
    .replace(/\/+$/, "");
  const parts = s.split("/").filter(Boolean);
  // Keep the last two path segments: "github.com/acme/api" → "acme/api",
  // "acme/api" → "acme/api". A bare token stays as-is so validation can reject it.
  return parts.length >= 2 ? parts.slice(-2).join("/") : s;
}

interface EgressEntry {
  id: string;
  scope: string;
  host: string;
  note: string | null;
  createdAt: number;
}

interface EgressListResult {
  entries: EgressEntry[];
  defaults: string[];
}

type ScopeMode = "org" | "repo";

export function EgressAllowlistTab() {
  const data = useQuery((api as any).egress.queries.listForOrg, {}) as EgressListResult | undefined;
  const addEntry = useMutation((api as any).egress.mutations.addEntry) as unknown as (args: {
    scope: string;
    host: string;
    note?: string;
  }) => Promise<unknown>;
  const removeEntry = useMutation((api as any).egress.mutations.removeEntry) as unknown as (args: {
    scope: string;
    host: string;
  }) => Promise<unknown>;

  const [host, setHost] = useState("");
  const [note, setNote] = useState("");
  const [scopeMode, setScopeMode] = useState<ScopeMode>("org");
  const [repoScope, setRepoScope] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const loading = data === undefined;
  const entries = data?.entries ?? [];
  const defaults = data?.defaults ?? [];
  // One write at a time: an add and a remove share `error`, so letting them
  // overlap lets one action's setError clobber the other's. Gate ALL write
  // controls on a single flag. (Gemini.)
  const isBusy = submitting || busyKey !== null;

  // Group custom entries: an "Organization-wide" group (scope === "*") and
  // one group per distinct repo scope, each headed by the owner/repo key.
  const { orgEntries, repoGroups } = useMemo(() => {
    const org: EgressEntry[] = [];
    const byRepo = new Map<string, EgressEntry[]>();
    for (const e of entries) {
      if (e.scope === ORG_SCOPE) {
        org.push(e);
      } else {
        const bucket = byRepo.get(e.scope) ?? [];
        bucket.push(e);
        byRepo.set(e.scope, bucket);
      }
    }
    const groups = Array.from(byRepo.entries())
      .map(([scope, items]) => ({ scope, items }))
      .sort((a, b) => a.scope.localeCompare(b.scope));
    return { orgEntries: org, repoGroups: groups };
  }, [entries]);

  const hasCustom = orgEntries.length > 0 || repoGroups.length > 0;

  async function handleAdd() {
    const cleanedHost = host.trim().toLowerCase();
    const scope = scopeMode === "org" ? ORG_SCOPE : normalizeRepoScope(repoScope);

    if (!cleanedHost) {
      setError("Enter a host to allow.");
      return;
    }
    if (!HOST_RE.test(cleanedHost)) {
      setError(
        `Invalid host "${host.trim()}". Use a hostname (api.example.com), a "*." wildcard, or an IPv4 address.`
      );
      return;
    }
    if (scopeMode === "repo") {
      if (!scope) {
        setError(
          "Enter the repo (owner/repo) for a repo-scoped entry, or switch to organization-wide."
        );
        return;
      }
      if (!REPO_SCOPE_RE.test(scope)) {
        setError(
          `Enter the repo as owner/repo (e.g. acme/widgets) — got "${repoScope.trim()}". This must match the session's repository identifier, not a browser URL.`
        );
        return;
      }
    }

    setSubmitting(true);
    setError(null);
    try {
      const trimmedNote = note.trim();
      await addEntry({
        scope,
        host: cleanedHost,
        ...(trimmedNote ? { note: trimmedNote } : {}),
      });
      // Clear inputs on success; keep the scope selection so an admin can
      // add several entries to the same scope without re-picking it.
      setHost("");
      setNote("");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to add egress entry.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRemove(entry: EgressEntry) {
    const key = `${entry.scope}::${entry.host}`;
    setBusyKey(key);
    setError(null);
    try {
      await removeEntry({ scope: entry.scope, host: entry.host });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to remove entry";
      setError(`Failed to remove ${entry.host}: ${msg}`);
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <div className="ac-egress-tab ac-vault-tab">
      <div className="ac-tab-header">
        <div>
          <h2 className="ac-tab-title">Egress allow-list</h2>
          <p className="ac-tab-subtitle">
            Outbound network destinations your agents are allowed to reach. Everything else is asked
            for approval or blocked, per your enforcement mode. Curated built-ins are always
            allowed.
          </p>
        </div>
      </div>

      {error && (
        <p className="ac-error-banner" role="alert">
          {error}
        </p>
      )}

      <section className="ac-tab-section" aria-label="Add egress destination">
        <h3>Add a destination</h3>
        <form
          className="ac-egress-add ac-mint-row"
          onSubmit={(e) => {
            e.preventDefault();
            void handleAdd();
          }}
        >
          <label className="ac-egress-field">
            <span>Host</span>
            <input
              type="text"
              value={host}
              onChange={(e) => setHost(e.target.value)}
              placeholder="api.example.com or *.example.com"
              aria-label="Host"
              disabled={isBusy}
            />
          </label>
          <label className="ac-egress-field">
            <span>Scope</span>
            <select
              value={scopeMode}
              onChange={(e) => setScopeMode(e.target.value as ScopeMode)}
              aria-label="Scope"
              disabled={isBusy}
            >
              <option value="org">Organization-wide</option>
              <option value="repo">Specific repo</option>
            </select>
          </label>
          {scopeMode === "repo" && (
            <label className="ac-egress-field">
              <span>Repo (owner/repo)</span>
              <input
                type="text"
                value={repoScope}
                onChange={(e) => setRepoScope(e.target.value)}
                placeholder="acme/widgets"
                aria-label="Repo (owner/repo)"
                disabled={isBusy}
              />
            </label>
          )}
          <label className="ac-egress-field">
            <span>Note (optional)</span>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Why this is allowed"
              aria-label="Note"
              disabled={isBusy}
            />
          </label>
          <button
            type="submit"
            className="ac-btn ac-btn--primary"
            disabled={isBusy || !host.trim()}
          >
            {submitting ? "Adding…" : "Add destination"}
          </button>
        </form>
      </section>

      <section className="ac-tab-section" aria-label="Custom egress destinations">
        <h3>Custom destinations</h3>
        {loading ? (
          <p className="ac-loading">Loading egress allow-list…</p>
        ) : !hasCustom ? (
          <div className="ac-empty-state">
            <p>
              No custom egress destinations yet. Add one above to let agents reach an outbound host
              that isn't covered by the built-in defaults.
            </p>
          </div>
        ) : (
          <>
            {orgEntries.length > 0 && (
              <EgressScopeGroup
                heading="Organization-wide"
                isRepo={false}
                entries={orgEntries}
                busyKey={busyKey}
                disabled={isBusy}
                onRemove={handleRemove}
              />
            )}
            {repoGroups.map((group) => (
              <EgressScopeGroup
                key={group.scope}
                heading={group.scope}
                isRepo
                entries={group.items}
                busyKey={busyKey}
                disabled={isBusy}
                onRemove={handleRemove}
              />
            ))}
          </>
        )}
      </section>

      <section className="ac-tab-section" aria-label="Built-in egress destinations">
        <h3>Built-in (always allowed)</h3>
        <p className="ac-tab-subtitle">
          Curated destinations Steerly always permits. These are read-only.
        </p>
        {loading ? (
          <p className="ac-loading">Loading built-in destinations…</p>
        ) : defaults.length === 0 ? (
          <div className="ac-empty-state">
            <p>No built-in destinations configured.</p>
          </div>
        ) : (
          <div className="ac-egress-chips" role="list" aria-label="Built-in destinations">
            {defaults.map((d) => (
              <span
                key={d}
                role="listitem"
                className="admin-chip admin-chip--neutral ac-egress-chip"
              >
                {d}
              </span>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function EgressScopeGroup({
  heading,
  isRepo,
  entries,
  busyKey,
  disabled,
  onRemove,
}: {
  heading: string;
  isRepo: boolean;
  entries: EgressEntry[];
  busyKey: string | null;
  disabled: boolean;
  onRemove: (entry: EgressEntry) => void;
}) {
  return (
    <div className="ac-egress-group">
      {/* Repo headings carry an owner/repo identifier — drop the uppercase
          transform so it stays readable + case-accurate. (Gemini.) */}
      <h4 className={`ac-egress-group__heading${isRepo ? " ac-egress-group__heading--repo" : ""}`}>
        {heading}
      </h4>
      <div className="ac-egress-chips" role="list" aria-label={`${heading} destinations`}>
        {entries.map((entry) => {
          const key = `${entry.scope}::${entry.host}`;
          const busy = busyKey === key;
          return (
            <span
              key={entry.id}
              role="listitem"
              className="admin-chip admin-chip--allow ac-egress-chip"
            >
              <span className="ac-egress-chip__host">{entry.host}</span>
              {entry.note && <span className="ac-egress-chip__note"> — {entry.note}</span>}
              <button
                type="button"
                className="ac-egress-chip__remove"
                onClick={() => onRemove(entry)}
                disabled={busy || disabled}
                aria-label={`Remove ${entry.host}`}
                title={`Remove ${entry.host}`}
              >
                {busy ? "…" : "×"}
              </button>
            </span>
          );
        })}
      </div>
    </div>
  );
}
